/**
 * ParticipantAgreementsTab — Phase 2C (Feb 2026)
 * Lists service agreements for the participant and exposes the extended
 * lifecycle actions (Send / Sign / Cancel / Replace / Activate / End)
 * when `service_agreement_workflow_enabled` is ON.
 *
 * Backward-compatible: when the flag is OFF, only legacy actions appear.
 */
import React, { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { onlyBranch } from '@/lib/singleBranch';
import { toast } from 'sonner';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { TrinitiDatePicker } from '@/components/triniti-datetime';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FileSignature, Send, CheckCircle2, XCircle, RefreshCw, ArrowRight, Wallet, Eye, FileText, Download } from 'lucide-react';
import { agreementDocumentFilename, downloadAgreementPdf, openAgreementPdfPreview, resolveAgreementFilename } from '@/lib/agreementDocumentFilename';
import { AgreementPreviewDialog } from './AgreementPreviewDialog';
import { FundingManagementFields, FundingBadge, EMPTY_FUNDING, fundingFromAgreement, fundingPayload } from './FundingManagementFields';
import { SignerSelector, EMPTY_SIGNER } from './SignerSelector';
import { ActivationReadinessBadge } from './ActivationReadinessBadge';
import { fmtMoney } from './OfficialAgreementLinesEditor';
import { AgreementServicesEditor, validateServices, servicesPayload } from './AgreementServicesEditor';
import { AgreementReadinessStrip, useCreateReadiness } from './AgreementReadinessStrip';
import { AgreementDocumentsPanel } from './AgreementDocumentsPanel';
import { awaitingReviewChip, nextStepHint, signingRowActions, canViewDocument, canLivePreview, approveGate, workflowFlagHint, saveDraftReason, regenerationGate, cancelDraftGate, activationMismatchLines, REGENERATION_ACTION } from './agreementRowActions';   // UA-2 / UA-4 / UA-8
import { useAppDialog } from '@/hooks/useAppDialog';   // UA-8 F30-B
import { scheduleChip, activationToast, canOpenScheduleHome } from './scheduleChipRules';   // RP-3 Option A
import { AgreementLineSchedulePanel } from './AgreementLineSchedulePanel';
import { AgreementHistoryPanel } from './AgreementHistoryPanel';
import { AgreementLineTermsEditor, editableLine, validateLineTerms, lineTermsPayload, rateBasisLabel, frequencyLabel, usesCatalogueRate, NOT_SET } from './AgreementLineTermsEditor';
import { friendlyError, friendlyErrorAsync, AGREEMENT_ACTION_LABELS } from '@/lib/friendlyCodes';

const STATUS_COLORS = {
  DRAFT: 'bg-slate-100 text-slate-700 border-slate-300',
  SENT: 'bg-amber-100 text-amber-700 border-amber-300',
  SENT_FOR_SIGNATURE: 'bg-amber-100 text-amber-700 border-amber-300',
  SIGNATURE_REVIEW: 'bg-violet-100 text-violet-700 border-violet-300',
  APPROVED_FOR_SIGNATURE: 'bg-sky-100 text-sky-700 border-sky-300',
  SIGNED: 'bg-blue-100 text-blue-700 border-blue-300',
  ACTIVE: 'bg-emerald-100 text-emerald-700 border-emerald-300',
  EXPIRED: 'bg-orange-100 text-orange-700 border-orange-300',
  ENDED: 'bg-zinc-100 text-zinc-700 border-zinc-300',
  REPLACED: 'bg-purple-100 text-purple-700 border-purple-300',
  CANCELLED: 'bg-red-100 text-red-700 border-red-300',
};

function StatusBadge({ status }) {
  const cls = STATUS_COLORS[status] || 'bg-gray-100 text-gray-700 border-gray-300';
  return (
    <Badge variant="outline" className={`${cls} text-xs`} data-testid={`agreement-status-${status}`}>
      {status}
    </Badge>
  );
}

export function ParticipantAgreementsTab({ participantId, onOpenSchedules }) {
  const { user } = useAuth();
  const perms = user?.permissions || [];
  const { dangerConfirm } = useAppDialog();   // UA-8 F30-B
  const [mismatch, setMismatch] = useState(null);   // UA-8 F30-B: { agreement, lines[] } from a RATE_AUTHORITY_MISMATCH refusal
  const canView = perms.includes('agreements.view');
  const canCreate = perms.includes('agreements.create');
  const canApprove = perms.includes('agreements.approve');
  const canEnd = perms.includes('agreements.end');
  const canExport = perms.includes('agreements.export');   // F50-3: download gating unchanged (same permission as the Documents panel)

  // Read Phase 2C flag from localStorage (populated at login from /api/production/feature-flags/mine)
  const [flagEnabled, setFlagEnabled] = useState(false);
  useEffect(() => {
    try {
      const flags = JSON.parse(localStorage.getItem('gc360_feature_flags') || '{}');
      setFlagEnabled(!!flags.service_agreement_workflow_enabled);
    } catch { /* ignore */ }
  }, []);

  const [agreements, setAgreements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);

  // Action dialogs
  const [sendOpen, setSendOpen] = useState(false);
  const [signOpen, setSignOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [replaceOpen, setReplaceOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [services, setServices] = useState([]);

  const [createForm, setCreateForm] = useState({ start_date: '', end_date: '', notes: '', template_id: '' });
  const [createServices, setCreateServices] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [templatesError, setTemplatesError] = useState(null);   // UA-2-F3
  const [detailsFocus, setDetailsFocus] = useState(null);         // UA-2-F1/F2: 'upload' | 'review' | 'schedule' (RP-3) when opened from a row action
  const [scheduleInfo, setScheduleInfo] = useState({});           // RP-3 Option A: agreement id → /schedules payload
  const openScheduleHome = (a) => {                               // RP-3 Option A: Schedule home when hosted in the participant page, else Details ▸ schedules panel
    if (onOpenSchedules) { onOpenSchedules(a?.id || null); return; }
    setDetailsFocus('schedule'); openDetails(a);
  };
  const [officialEnabled, setOfficialEnabled] = useState([]);
  const [officialLoading, setOfficialLoading] = useState(false);
  const [createBranchId, setCreateBranchId] = useState('');
  const [detailsData, setDetailsData] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [fundingPlans, setFundingPlans] = useState([]);
  const [branches, setBranches] = useState([]);
  const [createFunding, setCreateFunding] = useState(EMPTY_FUNDING);
  const readiness = useCreateReadiness({ participantId, branchId: createBranchId, startDate: createForm.start_date, endDate: createForm.end_date, fundingType: createFunding?.type, templateId: createForm.template_id, enabled: createOpen });
  const [fundingOpen, setFundingOpen] = useState(false);
  const [fundingForm, setFundingForm] = useState(EMPTY_FUNDING);
  const [fundingReason, setFundingReason] = useState('');
  const [fundingRefusal, setFundingRefusal] = useState(null);
  const fundingPostSignature = ['SIGNED', 'ACTIVE'].includes(selected?.status);
  const fundingCurrentlySet = (selected?.funding_management?.type || 'NOT_SET') !== 'NOT_SET';

  const [sendForm, setSendForm] = useState({ sent_to_email: '', review_date: '' });
  const [signForm, setSignForm] = useState({ ...EMPTY_SIGNER, signed_document_id: '' });
  const [cancelForm, setCancelForm] = useState({ reason: '' });
  const [replaceForm, setReplaceForm] = useState({ new_start_date: '', new_end_date: '', notes: '' });
  const [variationOpen, setVariationOpen] = useState(false);
  const [variationForm, setVariationForm] = useState({ start_date: '', end_date: '', reason: '' });
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({ start_date: '', end_date: '', notes: '', template_id: '' });
  const [editNewServices, setEditNewServices] = useState([]); // R3: add-only — new lines via the same AgreementServicesEditor as Create
  const [previewFor, setPreviewFor] = useState(null); // R1: agreement being previewed (row / Send dialog)
  // Parity R2: same readiness hook/endpoint for the DRAFT being edited (no second readiness implementation)
  const editReadiness = useCreateReadiness({ participantId, branchId: selected?.branch_id, startDate: editForm.start_date, endDate: editForm.end_date, fundingType: selected?.funding_management?.type, templateId: editForm.template_id || selected?.template_id, enabled: editOpen && !!selected });
  const [editLines, setEditLines] = useState([]);
  const [busy, setBusy] = useState(false);
  const [exporting, setExporting] = useState(false);

  // UA-4-F5/F6 (§7): the frozen document is the ONLY agreement PDF. Opens inline (agreements.view) — generate-first when
  // the status allows and no document exists; otherwise the server refuses with the §7 message naming the status.
  const openFrozenDocument = async (a, which = 'auto') => {
    const res = await api.get(`/agreements/${a.id}/documents/current/view`, { params: { which }, responseType: 'blob' });
    openAgreementPdfPreview(res.data);   // F50: preview stays "open in new tab"
  };
  // F50-2: explicit, reliable download of the frozen document with the shared filename convention
  // (`<agreement_no>-Unsigned|Signed.pdf`). Gated exactly as before: agreements.export (download route).
  const downloadFrozenDocument = async (a, which = 'unsigned') => {
    if (!canExport) return;
    setExporting(true);
    try {
      const res = await api.get(`/agreements/${a.id}/documents/current/download`, { params: { which }, responseType: 'blob' });
      const name = downloadAgreementPdf(res.data, resolveAgreementFilename(a.agreement_no, which, res.headers?.['content-disposition']));
      toast.success(`Downloaded ${name}`);
    } catch (e) { toast.error(await friendlyErrorAsync(e, 'Download failed')); } finally { setExporting(false); }
  };
  const blobError = (e, fallback) => friendlyErrorAsync(e, fallback);   // UA-6-F21: one error convention (code + message), blob bodies parsed
  const exportPdf = async (a) => {
    setExporting(true);
    try {
      try {
        await openFrozenDocument(a);
        toast.success('Frozen agreement document opened');
      } catch (e) {
        const detail = e.response?.data instanceof Blob ? JSON.parse(await e.response.data.text()) : e.response?.data;
        const err = detail?.error || detail?.detail || {};
        if (e.response?.status === 409 && err.code === 'NO_FROZEN_DOCUMENT' && err.details?.generatable && canCreate) {
          await api.post(`/agreements/${a.id}/documents/generate`, {});
          toast.success('Agreement document generated from the template and organisation branding');
          await openFrozenDocument(a);
        } else throw e;
      }
    } catch (e) {
      toast.error(await blobError(e, 'Export failed'));
    } finally {
      setExporting(false);
    }
  };

  const fetchAgreements = useCallback(async () => {
    if (!canView) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/agreements/by-participant/${participantId}`);
      const items = res.data?.data?.items || res.data?.items || res.data?.data || [];
      setAgreements(Array.isArray(items) ? items : []);
      // RP-3 Option A: schedule/shift counts for ACTIVE rows (GET /agreements/{id}/schedules), best effort
      const act = (Array.isArray(items) ? items : []).filter((a) => (a.effective_status || a.status) === 'ACTIVE');
      const infos = await Promise.allSettled(act.map((a) => api.get(`/agreements/${a.id}/schedules`)));
      setScheduleInfo(Object.fromEntries(act.map((a, i) => [a.id, infos[i].status === 'fulfilled' ? (infos[i].value.data?.data || null) : null])));
    } catch (e) {
      setError(e.response?.data?.error?.message || e.message || 'Failed to load agreements');
    } finally {
      setLoading(false);
    }
  }, [participantId, canView]);

  useEffect(() => { fetchAgreements(); }, [fetchAgreements]);

  const loadOfficialEnabled = useCallback(async (branchCode) => {
    if (!branchCode) { setOfficialEnabled([]); return; }
    setOfficialLoading(true);
    try {
      const res = await api.get('/branch-catalogue', { params: { branch_id: branchCode } });
      setOfficialEnabled((res.data.data || []).filter((x) => x.enabled));
    } catch { setOfficialEnabled([]); }
    finally { setOfficialLoading(false); }
  }, []);

  const loadServices = useCallback(async () => {
    try {
      const [fpRes, brRes, pRes] = await Promise.allSettled([
        api.get(`/funding/plans/by-participant/${participantId}`),
        api.get('/org/branches'),
        api.get(`/participants/${participantId}`),
      ]);
      if (fpRes.status === 'fulfilled') setFundingPlans(fpRes.value.data.data || []);
      let brs = [];
      if (brRes.status === 'fulfilled') { brs = brRes.value.data.data || []; setBranches(brs); }
      // Default the agreement branch to the participant's branch (stored as a branch CODE).
      const pBranch = pRes.status === 'fulfilled' ? (pRes.value.data?.data?.branch_id || pRes.value.data?.branch_id) : null;
      const defaultBranch = onlyBranch(brs)?.code || pBranch || brs[0]?.code || '';   // one branch: always that branch
      setCreateBranchId(defaultBranch);
      loadOfficialEnabled(defaultBranch);
      const res = await api.get('/services/catalog?is_active=true&limit=100&mode=selection');
      const items = res.data?.data?.items || res.data?.items || res.data?.data || [];
      setServices(Array.isArray(items) ? items : []);
      setTemplatesError(null);
      const tRes = await api.get('/agreements/templates').catch((e) => { setTemplatesError(e); return { data: { data: [] } }; });   // UA-2-F3: never report "no template" for a failed request
      const tpls = tRes.data?.data || []; setTemplates(tpls);
      // Day 24F-C: prefer an organisation template whose legal-review sections are complete; shared templates are samples
      setCreateForm((f) => (f.template_id ? f : { ...f, template_id: tpls.find((t) => !t.shared && !t.sample_state)?.id || tpls.find((t) => t.id === 'shared:STANDARD_NDIS')?.id || tpls[0]?.id || '' }));
    } catch { /* ignore */ }
  }, [participantId, loadOfficialEnabled]);
  // Day 23 DEF-23-04: funding plans / branches are also needed by Details ▸ Schedule (not only the create dialog)
  useEffect(() => { loadServices(); }, [loadServices]);

  const setCreatePeriod = (key, v) => {
    const prev = createForm[key]; const lineKey = key === 'start_date' ? 'service_start_date' : 'service_end_date';
    setCreateForm({ ...createForm, [key]: v });
    setCreateServices((ls) => ls.map((l) => (!l[lineKey] || l[lineKey] === prev ? { ...l, [lineKey]: v } : l)));
  };

  const onCreate = async () => {
    if (!createForm.start_date || !createForm.end_date) { toast.error('Start and end dates required'); return; }
    if (!createForm.template_id) { toast.error('Select a Service Agreement template'); return; }
    const svcErr = validateServices(createServices, officialEnabled, { start: createForm.start_date, end: createForm.end_date });
    if (svcErr) { toast.error(svcErr); return; }
    setBusy(true);
    try {
      const res = await api.post('/agreements', {
        participant_id: participantId,
        start_date: createForm.start_date,
        end_date: createForm.end_date,
        notes: createForm.notes || '',
        branch_id: createBranchId || undefined,
        template_id: createForm.template_id,
        line_items: servicesPayload(createServices, officialEnabled),
        funding_management: fundingPayload(createFunding),
      });
      const n = (res.data?.data?.line_items || []).length;
      toast.success(`Draft agreement created${n ? ` with ${n} service${n === 1 ? '' : 's'}` : ' (no services yet)'}`);
      setCreateOpen(false);
      setCreateServices([]);
      setCreateForm({ start_date: '', end_date: '', notes: '', template_id: '' });
      setCreateFunding(EMPTY_FUNDING);
      await fetchAgreements();
    } catch (e) {
      const d = e.response?.data;
      const msg = d?.detail?.error?.message || d?.detail?.message || d?.error?.message || (typeof d?.detail === 'string' ? d.detail : null) || e.message;
      const code = d?.detail?.error?.code || d?.detail?.code || d?.error?.code;
      toast.error(`Create failed: ${msg}${code ? ` [${code}]` : ''}`);
    } finally {
      setBusy(false);
    }
  };

  const onSaveFunding = async () => {
    setBusy(true);
    if (fundingPostSignature && !fundingCurrentlySet && !fundingReason.trim()) { toast.error('A reason is required to set the funding method on a signed agreement'); return; }
    setFundingRefusal(null);
    try {
      const saved = await api.put(`/agreements/${selected.id}/funding-management`, { ...fundingPayload(fundingForm), reason: fundingPostSignature ? fundingReason.trim() || null : null });
      toast.success('Funding management saved');
      setFundingOpen(false); setFundingReason('');
      if (saved.data?.data) setSelected((prev) => (prev && prev.id === selected.id ? { ...prev, ...saved.data.data } : prev)); // keeps the open Edit dialog summary/readiness current
      await fetchAgreements();
    } catch (e) {
      const d = e.response?.data?.detail;
      setFundingRefusal({ code: d?.code, message: d?.message || (typeof d === 'string' ? d : e.message) });
      toast.error(`Save failed: ${d?.message || d || e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const callAction = async (action, body) => {
    if (!selected) return;
    setBusy(true);
    try {
      const res = await api.post(`/agreements/${selected.id}/${action}`, body || {});
      toast.success(`Agreement ${action}: success`);
      await fetchAgreements();
      return res.data || true;
    } catch (e) {
      toast.error(`${action} failed: ${friendlyError(e)}`);
      return false;
    } finally {
      setBusy(false);
    }
  };

  // UA-2 (§6): row-level signing actions. MANUAL issue = same transition with signing_mode=MANUAL (no e-mail, no dispatch), then download the frozen unsigned PDF.
  const onSigningRowAction = async (a, key) => {
    setSelected(a);
    if (key === 'generate_document') { setDetailsFocus('generate'); openDetails(a); return; }   // UA-6-F20
    if (key === 'send_online') { setSendOpen(true); return; }
    if (key === 'issue_manual') {
      setBusy(true);
      try {
        await api.post(`/agreements/${a.id}/send`, { signing_mode: 'MANUAL' });
        await fetchAgreements();
        if (canExport) {   // F50-2: download the named frozen unsigned file for printing; preview remains available via View document
          toast.success('Issued for manual signing — downloading the frozen unsigned PDF');
          await downloadFrozenDocument(a, 'unsigned');
        } else {
          toast.success('Issued for manual signing — opening the frozen unsigned PDF');
          await openFrozenDocument(a, 'unsigned');
        }
      } catch (e) { toast.error(await friendlyErrorAsync(e, 'Could not issue for manual signing')); } finally { setBusy(false); }   // UA-6-F21
      return;
    }
    setDetailsFocus(key === 'upload_signed' ? 'upload' : 'review');
    openDetails(a);
  };
  const toastEmail = (email, to) => {
    if (!email) {   // #19: the backend found no address (none entered, none on the participant/representative)
      toast.warning('Recorded as sent, but no email was sent — the participant and their representative have no email address. Add one, then use Resend.', { duration: 10000 });
      return;
    }
    to = email.to || to;
    if (email.status === 'SENT') toast.success(`Agreement emailed to ${to}${email.provider === 'MOCK' ? ' (mock provider — no real email sent)' : ''}`);
    else toast.error(`Agreement recorded as sent, but the email to ${to} failed: ${email.error || 'unknown error'}. Use Resend to retry.`);
  };
  const onSend = async () => {
    const to = sendForm.sent_to_email.trim();
    const res = await callAction('send', { ...sendForm, sent_to_email: to || null, signing_mode: 'ONLINE' });
    if (res) {
      toastEmail(res.email, to);
      setSendOpen(false);
      setSendForm({ sent_to_email: '', review_date: '' });
    }
  };
  const onSign = async () => {
    if (!signForm.signed_by_name.trim()) { toast.error('Signed-by name is required'); return; }
    if (signForm.signer_type === 'REPRESENTATIVE' && !signForm.representative_contact_id) { toast.error('Select the representative who signed'); return; }
    if (await callAction('sign', signForm)) {
      setSignOpen(false);
      setSignForm({ ...EMPTY_SIGNER, signed_document_id: '' });
    }
  };
  const onCancel = async () => {
    if (!cancelForm.reason.trim()) { toast.error('Cancellation reason is required'); return; }
    if (await callAction('cancel', cancelForm)) {
      setCancelOpen(false);
      setCancelForm({ reason: '' });
    }
  };
  const onReplace = async () => {
    if (!replaceForm.new_start_date || !replaceForm.new_end_date) { toast.error('Start and end dates required'); return; }
    if (await callAction('replace', replaceForm)) {
      setReplaceOpen(false);
      setReplaceForm({ new_start_date: '', new_end_date: '', notes: '' });
    }
  };
  const onLifecycle = async (a, action) => {
    const needsReason = ['request_changes', 'decline', 'suspend', 'terminate', 'cancel_draft'].includes(action);
    const reason = needsReason ? window.prompt(`Reason for ${action.replace(/_/g, ' ')}`) : null;
    if (needsReason && !reason) return;
    setBusy(true);
    try {
      await api.post(`/agreements/${a.id}/transition`, { action, reason });
      toast.success(`Agreement ${action.replace(/_/g, ' ')} complete`);
      await fetchAgreements();
    } catch (e) {
      toast.error(friendlyError(e, 'Lifecycle action failed'));
    } finally { setBusy(false); }
  };
  const onVariation = async () => {
    if (!selected || !variationForm.start_date || !variationForm.end_date || !variationForm.reason.trim()) { toast.error('New period and reason are required'); return; }
    setBusy(true);
    try {
      await api.post(`/agreements/${selected.id}/variation`, variationForm);
      toast.success('Variation created as a new DRAFT version — the current agreement stays in force until the variation is signed and activated');
      setVariationOpen(false); setVariationForm({ start_date: '', end_date: '', reason: '' });
      await fetchAgreements();
    } catch (e) { toast.error(friendlyError(e, 'Could not start variation')); } finally { setBusy(false); }
  };

  // Batch 2 L2/L4 — resend (same frozen document) and renewal (new DRAFT version from ACTIVE/EXPIRED)
  const onResend = async (a) => {
    setBusy(true);
    try {
      const res = await api.post(`/agreements/${a.id}/resend`, {});
      toast.success('Agreement resent for signature (same frozen document) — recorded in the lifecycle history');
      toastEmail(res.data?.email, res.data?.data?.sent_to_email);
      await fetchAgreements();
    } catch (e) { toast.error(friendlyError(e, 'Could not resend')); } finally { setBusy(false); }
  };
  const onRenew = async (a) => {
    const reason = window.prompt('Renewal reason (a new DRAFT version is created; it must be approved, signed and activated)');
    if (!reason || !reason.trim()) return;
    setBusy(true);
    try {
      await api.post(`/agreements/${a.id}/renew`, { reason: reason.trim() });
      toast.success('Renewal created as a new DRAFT version — the current agreement stays in force until the renewal is signed and activated');
      await fetchAgreements();
    } catch (e) { toast.error(friendlyError(e, 'Could not start renewal')); } finally { setBusy(false); }
  };

  const onActivate = async (a) => {
    setSelected(a);
    setBusy(true);
    try {
      await api.post(`/agreements/${a.id}/activate`);
      toast.success('Agreement activated', activationToast(perms, () => openScheduleHome(a)));   // RP-3 Option A
      await fetchAgreements();
    } catch (e) {
      const lines = activationMismatchLines(e);   // UA-8 F30-B: list details.lines[] and offer the recovery path instead of a generic toast
      if (lines) setMismatch({ agreement: a, lines });
      else toast.error(`Activate failed: ${friendlyError(e)}`);
    } finally {
      setBusy(false);
    }
  };
  // UA-8 F30-B: SIGNED / SIGNATURE_REVIEW (never ACTIVE) → this version TERMINATED (SUPERSEDED_BEFORE_ACTIVATION) + a new DRAFT version to regenerate, re-sign, review and activate.
  const onRegenerate = async (a) => {
    const result = await dangerConfirm({
      title: 'Return to draft for regeneration (new version)?',
      description: `${a.agreement_no || 'This version'} will be closed as superseded before activation — its signed document and history are preserved. A new DRAFT version (same period, lines, funding and template) is created; it must be generated, signed, reviewed and activated again.`,
      confirmLabel: 'Create new draft version', cancelLabel: 'Keep as is', reasonRequired: true, testId: 'agreement-regenerate-confirm',
    });
    if (!result) return;
    setBusy(true);
    try {
      const r = await api.post(`/agreements/${a.id}/transition`, { action: REGENERATION_ACTION, reason: result.reason });
      toast.success(`New draft version ${r.data?.data?.agreement_no || ''} created — generate the document, sign, review and activate it`.replace('  ', ' '));
      setMismatch(null);
      await fetchAgreements();
    } catch (e) { toast.error(friendlyError(e, 'Could not return the agreement to draft')); } finally { setBusy(false); }
  };
  const onEnd = async (a) => {
    setBusy(true);
    try {
      await api.post(`/agreements/${a.id}/end`);
      toast.success('Agreement ended');
      await fetchAgreements();
    } catch (e) {
      const msg = e.response?.data?.error?.message || e.response?.data?.detail || e.message;
      toast.error(`End failed: ${msg}`);
    } finally {
      setBusy(false);
    }
  };

  // Day 24e: lines whose dates mirror the agreement period follow a period change (others keep their explicit dates; backend fails closed if out of range)
  const setEditPeriod = (key, v) => {
    const prev = editForm[key]; const lineKey = key === 'start_date' ? 'service_start_date' : 'service_end_date';
    setEditForm({ ...editForm, [key]: v });
    setEditLines((ls) => ls.map((l) => (!l[lineKey] || l[lineKey] === prev ? { ...l, [lineKey]: v } : l)));
  };

  const onEditSave = async () => {
    if (!selected) return;
    if (!editForm.start_date || !editForm.end_date) { toast.error('Start and end dates required'); return; }
    const lineErr = validateLineTerms(editLines);
    if (lineErr) { toast.error(lineErr); return; }
    const svcErr = validateServices(editNewServices, officialEnabled, { start: editForm.start_date, end: editForm.end_date });
    if (svcErr) { toast.error(svcErr); return; }
    setBusy(true);
    try {
      // existing lines keep their agreement_line_id (terms payload); new services are appended and receive new ids server-side
      const lineItems = [...lineTermsPayload(editLines), ...servicesPayload(editNewServices, officialEnabled)];
      const templateChanged = !!editForm.template_id && editForm.template_id !== (selected.template_id || '');
      let hadCurrentDoc = false;
      try { const d = await api.get(`/agreements/${selected.id}/documents`); hadCurrentDoc = (d.data?.data || []).some((x) => !x.superseded); } catch { /* documents panel will still show the regenerate notice */ }
      await api.put(`/agreements/${selected.id}`, {
        start_date: editForm.start_date,
        end_date: editForm.end_date,
        notes: editForm.notes || '',
        ...(templateChanged ? { template_id: editForm.template_id } : {}),
        ...(lineItems.length ? { line_items: lineItems } : {}),
      });
      if (hadCurrentDoc) toast.warning('Agreement changed — regenerate document before sending/signing.', { duration: 8000 });
      else toast.success('Agreement updated');
      setEditOpen(false);
      await fetchAgreements();
    } catch (e) {
      const msg = friendlyError(e, 'Update failed');
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const openDetails = async (a) => {
    setSelected(a);
    setDetailsData(null);
    setDetailsOpen(true);
    setDetailsLoading(true);
    try {
      const res = await api.get(`/agreements/${a.id}`);
      setDetailsData(res.data?.data || null);
    } catch { setDetailsData(null); }
    finally { setDetailsLoading(false); }
  };

  if (!canView) {
    return <div className="text-sm text-muted-foreground" data-testid="agreements-no-permission">You don't have permission to view agreements.</div>;
  }

  return (
    <div className="space-y-4" data-testid="agreements-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold flex items-center gap-2"><FileSignature className="h-5 w-5" /> Service Agreements</h2>
          {workflowFlagHint(flagEnabled) && <p className="text-[11px] text-amber-700" data-testid="agreement-workflow-flag-off-hint">{workflowFlagHint(flagEnabled)}</p>}
          {flagEnabled && (
            <p className="text-xs text-muted-foreground" data-testid="agreement-extended-mode">
              Extended lifecycle enabled (SENT / SIGNED / EXPIRED / REPLACED / CANCELLED)
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={fetchAgreements} disabled={loading} data-testid="agreements-refresh">
            <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
          {canCreate && (
            <Button size="sm" onClick={() => { setCreateOpen(true); setCreateServices([]); loadServices(); }} data-testid="agreement-create-button">
              <FileSignature className="h-3.5 w-3.5 mr-1" /> New Agreement
            </Button>
          )}
        </div>
      </div>

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="agreements-error">{error}</div>
      )}

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Agreement No.</TableHead>
                <TableHead>Period</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Funding</TableHead>
                <TableHead>Signed By</TableHead>
                <TableHead>Lines</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {agreements.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground" data-testid="agreements-empty">No service agreements yet.</TableCell></TableRow>
              ) : agreements.map(a => (
                <TableRow key={a.id} data-testid={`agreement-row-${a.id}`}>
                  <TableCell className="font-mono text-xs">{a.agreement_no || a.id?.slice(-6)}</TableCell>
                  <TableCell className="text-xs">{a.start_date} → {a.end_date}</TableCell>
                  <TableCell><StatusBadge status={a.status} />
                    {(() => { const chip = scheduleChip(a, scheduleInfo[a.id]); return chip ? (
                      <button type="button" disabled={!chip.action || !canOpenScheduleHome(perms)} onClick={() => openScheduleHome(a)} title={chip.hint || ''}
                        className={`block mt-1 text-[10px] rounded border px-1 ${chip.tone === 'amber' ? 'bg-amber-50 text-amber-800 border-amber-300' : chip.tone === 'emerald' ? 'bg-emerald-50 text-emerald-700 border-emerald-300' : 'text-muted-foreground'}`}
                        data-testid={`agreement-schedule-chip-${a.id}`}>{chip.label}{chip.action && canOpenScheduleHome(perms) ? ' · Schedule' : ''}</button>
                    ) : null; })()}
                  </TableCell>
                  <TableCell><FundingBadge type={a.funding_management?.type} /></TableCell>
                  <TableCell className="text-xs">
                    {a.signed_by_name ? (
                      <span>
                        <span className="font-medium">{a.signed_by_name}</span>
                        {a.signed_by_role && <span className="text-muted-foreground"> ({a.signed_by_role})</span>}
                      </span>
                    ) : <span className="text-muted-foreground">—</span>}
                  </TableCell>
                  <TableCell className="text-xs">{(a.line_items || []).length}</TableCell>
                  <TableCell className="text-right space-x-1">
                    {Array.isArray(a.allowed_actions) && a.allowed_actions.length > 0 && (
                      <Select onValueChange={(action) => onLifecycle(a, action)} disabled={busy}>
                        <SelectTrigger className="h-8 w-[170px] text-xs" data-testid={`agreement-lifecycle-${a.id}`}><SelectValue placeholder="Lifecycle…" /></SelectTrigger>
                        <SelectContent>
                          {a.allowed_actions.filter((x) => !['send', 'sign', 'activate', 'cancel_draft', REGENERATION_ACTION].includes(x)).map((x) => (
                            <SelectItem key={x} value={x} data-testid={`agreement-lifecycle-${a.id}-${x}`}>{AGREEMENT_ACTION_LABELS[x] || x.replace(/_/g, ' ')}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => openDetails(a)} data-testid={`agreement-details-${a.id}`}>
                      Details
                    </Button>
                    {['DRAFT', 'SENT', 'SENT_FOR_SIGNATURE', 'SIGNATURE_REVIEW', 'SIGNED', 'ACTIVE', 'INTERNAL_REVIEW', 'CHANGES_REQUIRED', 'APPROVED_FOR_SIGNATURE'].includes(a.status) && canCreate && (
                      <Button size="sm" variant="ghost" onClick={() => { setSelected(a); setFundingForm(fundingFromAgreement(a)); setFundingOpen(true); }} disabled={busy} data-testid={`agreement-funding-${a.id}`}>
                        <Wallet className="h-3 w-3 mr-1" /> Funding
                      </Button>
                    )}
                    {['DRAFT', 'INTERNAL_REVIEW', 'CHANGES_REQUIRED'].includes(a.status) && canCreate && (
                      <Button size="sm" variant="ghost" onClick={() => { setSelected(a); setEditForm({ start_date: a.start_date || '', end_date: a.end_date || '', notes: a.notes || '', template_id: a.template_id || '' }); setEditLines((a.line_items || []).map(editableLine)); setEditNewServices([]); if (a.branch_id) loadOfficialEnabled(a.branch_id); setEditOpen(true); }} disabled={busy} data-testid={`agreement-edit-${a.id}`}>
                        Edit
                      </Button>
                    )}
                    {canLivePreview(a) && (
                      <Button size="sm" variant="ghost" onClick={() => setPreviewFor(a)} disabled={busy} title="Live render of the current agreement data on the template — nothing is stored; the frozen document may differ" data-testid={`agreement-preview-${a.id}`}>
                        <Eye className="h-3 w-3 mr-1" /> Preview (unsaved render)
                      </Button>
                    )}
                    {canViewDocument(a) && (
                      <Button size="sm" variant="ghost" onClick={() => exportPdf(a)} disabled={busy || exporting} title="Opens the current frozen agreement document (signed copy when present) — template + organisation branding as generated" data-testid={`agreement-view-document-${a.id}`}>
                        <FileText className="h-3 w-3 mr-1" /> View document
                      </Button>
                    )}
                    {canViewDocument(a) && (
                      <Button size="sm" variant="ghost" onClick={() => downloadFrozenDocument(a, a.status === 'ACTIVE' || a.status === 'SIGNED' ? 'signed' : 'unsigned')} disabled={busy || exporting || !canExport} title={canExport ? `Download the frozen document as ${agreementDocumentFilename(a.agreement_no, a.status === 'ACTIVE' || a.status === 'SIGNED' ? 'signed' : 'unsigned')}` : 'Requires the agreements.export permission — use View document to open it inline'} data-testid={`agreement-download-${a.id}`}>
                        <Download className="h-3 w-3 mr-1" /> Download
                      </Button>
                    )}
                    {signingRowActions(a, { canCreate, canApprove }, flagEnabled).map((act) => (
                      <Button key={act.key} size="sm" variant="outline" title={act.disabled ? act.reason : act.hint} disabled={busy || act.disabled} aria-disabled={act.disabled || undefined} onClick={() => !act.disabled && onSigningRowAction(a, act.key)} data-testid={`agreement-${act.key.replace('_', '-')}-${a.id}`}>
                        {act.key === 'send_online' ? <Send className="h-3 w-3 mr-1" /> : <FileSignature className="h-3 w-3 mr-1" />} {act.label}{act.disabled && <span className="ml-1 text-[10px] text-muted-foreground" data-testid={`agreement-${act.key.replace('_', '-')}-reason-${a.id}`}>· {act.reason}</span>}
                      </Button>
                    ))}
                    {(() => { const chip = awaitingReviewChip(a); return chip ? <Badge variant="outline" className="bg-violet-50 text-violet-700 border-violet-300 text-[10px]" title={chip.detail} data-testid={`agreement-awaiting-review-${a.id}`}>{chip.label} · {chip.detail}</Badge> : null; })()}
                    {flagEnabled && nextStepHint(a) && <span className="basis-full text-[11px] text-muted-foreground" data-testid={`agreement-next-step-${a.id}`}>{nextStepHint(a)}</span>}
                    {flagEnabled && ['DRAFT', 'INTERNAL_REVIEW', 'CHANGES_REQUIRED'].includes(a.status) && canCreate && (
                      <Button size="sm" variant="outline" disabled title="This agreement must be approved for signature (Submit for review → Approve) before it can be sent" data-testid={`agreement-send-blocked-${a.id}`}>
                        <Send className="h-3 w-3 mr-1" /> Send · approval required
                      </Button>
                    )}
                    {flagEnabled && ['SENT', 'SENT_FOR_SIGNATURE'].includes(a.status) && (
                      <Button size="sm" variant="outline" onClick={() => { if (canApprove) { setSelected(a); setSignOpen(true); } }} disabled={busy || !canApprove} title={approveGate(canApprove).reason || 'Record the typed signature'} data-testid={`agreement-sign-${a.id}`}>
                        <CheckCircle2 className="h-3 w-3 mr-1" /> Sign{!canApprove && <span className="ml-1 text-[10px] text-muted-foreground">· approver only</span>}
                      </Button>
                    )}
                    {((a.status === 'DRAFT' && !flagEnabled) || a.status === 'SIGNED') && (
                      <>
                        {canApprove && <ActivationReadinessBadge agreementId={a.id} refreshKey={a.updated_at} />}
                        <Button size="sm" onClick={() => canApprove && onActivate(a)} disabled={busy || !canApprove} title={approveGate(canApprove).reason || 'Activate the signed agreement'} data-testid={`agreement-activate-${a.id}`}>
                          <ArrowRight className="h-3 w-3 mr-1" /> Activate{!canApprove && <span className="ml-1 text-[10px] opacity-80">· approver only</span>}
                        </Button>
                      </>
                    )}
                    {regenerationGate(a, canApprove).visible && (   // UA-8 F30-B
                      <Button size="sm" variant="outline" onClick={() => !regenerationGate(a, canApprove).disabled && onRegenerate(a)} disabled={busy || regenerationGate(a, canApprove).disabled}
                        title={regenerationGate(a, canApprove).reason || 'Close this signed version as superseded before activation and create a new draft version to regenerate and re-sign'} data-testid={`agreement-regenerate-${a.id}`}>
                        <RefreshCw className="h-3 w-3 mr-1" /> Return to draft for regeneration{regenerationGate(a, canApprove).disabled && <span className="ml-1 text-[10px] opacity-80">· approver only</span>}
                      </Button>
                    )}
                    {cancelDraftGate(a, canCreate, flagEnabled).visible && (
                      <Button size="sm" variant="ghost" onClick={() => { if (!cancelDraftGate(a, canCreate, flagEnabled).disabled) { setSelected(a); setCancelOpen(true); } }} disabled={busy || cancelDraftGate(a, canCreate, flagEnabled).disabled}
                        title={cancelDraftGate(a, canCreate, flagEnabled).reason || 'Cancel this unsigned / not-yet-activated version (reason required)'} data-testid={`agreement-cancel-${a.id}`}>
                        <XCircle className="h-3 w-3 mr-1" /> Cancel
                      </Button>
                    )}
                    {flagEnabled && a.status === 'ACTIVE' && canApprove && (
                      <Button size="sm" variant="outline" onClick={() => { setSelected(a); setReplaceOpen(true); }} disabled={busy} data-testid={`agreement-replace-${a.id}`}>
                        <RefreshCw className="h-3 w-3 mr-1" /> Replace
                      </Button>
                    )}
                    {flagEnabled && a.status === 'SENT_FOR_SIGNATURE' && canCreate && (
                      <Button size="sm" variant="outline" onClick={() => onResend(a)} disabled={busy} data-testid={`agreement-resend-${a.id}`}>
                        <Send className="h-3 w-3 mr-1" /> Resend
                      </Button>
                    )}
                    {['ACTIVE', 'EXPIRED'].includes(a.effective_status || a.status) && canCreate && (
                      <Button size="sm" variant="outline" onClick={() => onRenew(a)} disabled={busy} data-testid={`agreement-renew-${a.id}`}>
                        Renew
                      </Button>
                    )}
                    {(a.effective_status || a.status) === 'ACTIVE' && canCreate && (
                      <Button size="sm" variant="outline" onClick={() => { setSelected(a); setVariationForm({ start_date: a.start_date || '', end_date: a.end_date || '', reason: '' }); setVariationOpen(true); }} disabled={busy} data-testid={`agreement-variation-${a.id}`}>
                        Variation
                      </Button>
                    )}
                    {a.status === 'ACTIVE' && canEnd && (
                      <Button size="sm" variant="ghost" onClick={() => onEnd(a)} disabled={busy} data-testid={`agreement-end-${a.id}`}>
                        End
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* SEND dialog */}
      <Dialog open={!!mismatch} onOpenChange={(o) => { if (!o) setMismatch(null); }}>
        <DialogContent data-testid="activation-mismatch-dialog">
          <DialogHeader>
            <DialogTitle>Unable to activate agreement</DialogTitle>
            <DialogDescription>The contractual pricing of one or more lines no longer matches the signed document, so this signed version cannot be activated. The signed document is unchanged.</DialogDescription>
          </DialogHeader>
          <ul className="space-y-1.5 text-sm" data-testid="activation-mismatch-lines">
            {(mismatch?.lines || []).map((l, i) => (
              <li key={i} className="rounded border px-2 py-1.5" data-testid={`activation-mismatch-line-${l.code}`}>
                <div className="font-medium">{l.service_name}</div>
                <div>{l.text}</div>
                <div className="text-[11px] text-muted-foreground">Reference: {l.code}{l.detail ? ` — ${l.detail}` : ''}</div>
              </li>
            ))}
          </ul>
          <p className="text-xs text-muted-foreground">Next step: return this version to draft for regeneration — a new draft version is created to correct the lines, regenerate the document, sign, review and activate. {!canApprove && regenerationGate(mismatch?.agreement, canApprove).reason}</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMismatch(null)} data-testid="activation-mismatch-close">Close</Button>
            <Button onClick={() => mismatch && onRegenerate(mismatch.agreement)} disabled={busy || !mismatch || regenerationGate(mismatch?.agreement, canApprove).disabled} data-testid="activation-mismatch-regenerate"><RefreshCw className="h-3 w-3 mr-1" /> Return to draft for regeneration</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={sendOpen} onOpenChange={setSendOpen}>
        <DialogContent data-testid="agreement-send-dialog">
          <DialogHeader>
            <DialogTitle>Send agreement {selected?.agreement_no} for e-signature</DialogTitle>
            <DialogDescription>Records the agreement as issued for online signing and emails the frozen agreement PDF to sign and return — to the address entered, otherwise to the participant's representative or the participant's own email. For paper signing use "Download / print for manual signing".</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Send to email (optional — leave blank to use the representative's or participant's email)</Label>
              <Input type="email" value={sendForm.sent_to_email} onChange={e => setSendForm({ ...sendForm, sent_to_email: e.target.value })} data-testid="agreement-send-email" /></div>
            <div><Label>Review date (optional)</Label>
              <TrinitiDatePicker value={sendForm.review_date} onChange={v => setSendForm({ ...sendForm, review_date: v })} testId="agreement-send-review-date" /></div>
          </div>
          <DialogFooter className="sm:justify-between">
            <Button variant="outline" onClick={() => setPreviewFor(selected)} disabled={busy || !selected} data-testid="agreement-send-preview"><Eye className="h-3.5 w-3.5 mr-1" /> Preview Agreement</Button>
            <div className="flex gap-2">
              <Button variant="ghost" onClick={() => setSendOpen(false)} disabled={busy}>Cancel</Button>
              <Button onClick={onSend} disabled={busy} data-testid="agreement-send-submit">Send</Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* SIGN dialog */}
      <Dialog open={signOpen} onOpenChange={setSignOpen}>
        <DialogContent data-testid="agreement-sign-dialog">
          <DialogHeader>
            <DialogTitle>Record signature — {selected?.agreement_no}</DialogTitle>
            <DialogDescription>Capture the signatory details to transition this agreement to SIGNED.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <SignerSelector agreementId={selected?.id} value={signForm} onChange={(v) => setSignForm({ ...signForm, ...v })} />
            <div><Label>Signed document ID (optional — must be a SERVICE_AGREEMENT doc)</Label>
              <Input value={signForm.signed_document_id} onChange={e => setSignForm({ ...signForm, signed_document_id: e.target.value })} placeholder="document _id" data-testid="agreement-sign-doc-id" /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setSignOpen(false)} disabled={busy}>Cancel</Button>
            <Button onClick={onSign} disabled={busy || !signForm.signed_by_name.trim()} data-testid="agreement-sign-submit">Record Signature</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CANCEL dialog */}
      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent data-testid="agreement-cancel-dialog">
          <DialogHeader>
            <DialogTitle>Cancel agreement {selected?.agreement_no}</DialogTitle>
            <DialogDescription>This transitions the agreement to CANCELLED. The audit trail will record your reason.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Reason *</Label>
            <Textarea value={cancelForm.reason} onChange={e => setCancelForm({ reason: e.target.value })} rows={3} data-testid="agreement-cancel-reason" required />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCancelOpen(false)} disabled={busy}>Close</Button>
            <Button variant="destructive" onClick={onCancel} disabled={busy || !cancelForm.reason.trim()} data-testid="agreement-cancel-submit">Confirm Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* REPLACE dialog */}
      <Dialog open={replaceOpen} onOpenChange={setReplaceOpen}>
        <DialogContent data-testid="agreement-replace-dialog">
          <DialogHeader>
            <DialogTitle>Replace agreement {selected?.agreement_no}</DialogTitle>
            <DialogDescription>The current ACTIVE agreement will be marked REPLACED and a new DRAFT created for the period below. Line items inherit by default.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>New start date *</Label>
                <TrinitiDatePicker value={replaceForm.new_start_date} onChange={v => setReplaceForm({ ...replaceForm, new_start_date: v })} testId="agreement-replace-start" /></div>
              <div><Label>New end date *</Label>
                <TrinitiDatePicker value={replaceForm.new_end_date} onChange={v => setReplaceForm({ ...replaceForm, new_end_date: v })} testId="agreement-replace-end" /></div>
            </div>
            <div><Label>Notes (optional)</Label>
              <Textarea value={replaceForm.notes} onChange={e => setReplaceForm({ ...replaceForm, notes: e.target.value })} rows={2} data-testid="agreement-replace-notes" /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setReplaceOpen(false)} disabled={busy}>Cancel</Button>
            <Button onClick={onReplace} disabled={busy || !replaceForm.new_start_date || !replaceForm.new_end_date} data-testid="agreement-replace-submit">Create Replacement</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AgreementPreviewDialog open={!!previewFor} onOpenChange={(o) => { if (!o) setPreviewFor(null); }} agreement={previewFor} templateId={previewFor?.template_id} />

      {/* CREATE dialog — Day 24e: Template → Details → Services (+ Add Service: NDIS | Custom) → Save Draft */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="agreement-create-dialog">
          <DialogHeader>
            <DialogTitle>Create Service Agreement (DRAFT)</DialogTitle>
            <DialogDescription data-testid="agreement-create-description">Choose the agreement template, enter the agreement details, then add the services. A draft can be saved once the participant profile is complete (legal name, date of birth and 9-digit NDIS number); funding, services and the remaining checks below can be completed before signature and activation.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <AgreementReadinessStrip readiness={readiness} perms={perms} extra={[{ key: 'services', label: 'Services', state: createServices.length ? 'OK' : 'WARN', detail: createServices.length ? `${createServices.length} service(s) on this draft` : 'No services added yet — required before signature' }]} />
            <div className="space-y-1">
              <Label className="text-sm font-semibold">1. Template *</Label>
              <Select value={createForm.template_id} onValueChange={(v) => setCreateForm({ ...createForm, template_id: v })}>
                <SelectTrigger data-testid="agreement-create-template"><SelectValue placeholder="Select a Service Agreement template" /></SelectTrigger>
                <SelectContent>{templates.map((t) => <SelectItem key={t.id} value={t.id} data-testid={`agreement-create-template-${t.id}`}>{t.name}{t.shared ? ' (shared sample)' : ` (organisation · v${t.currentVersion || 1})`}{t.sample_state ? ' — SAMPLE, not for signature' : ''}</SelectItem>)}</SelectContent>
              </Select>
              {templatesError && (() => { const code = templatesError?.response?.data?.detail?.code || templatesError?.response?.data?.error?.code || templatesError?.response?.status || 'ERROR'; const msg = templatesError?.response?.data?.detail?.message || templatesError?.response?.data?.error?.message || templatesError.message; return (
                <p className="text-xs text-red-700" data-testid="agreement-create-templates-error">Templates could not be loaded — <span className="font-mono">{String(code)}</span>: {code === 'ORG_CONTEXT_REQUIRED' ? 'Your user is not bound to an organisation; agreements are organisation-scoped — use an organisation-bound user.' : msg}</p>); })()}
              {!templatesError && templates.length === 0 && <p className="text-xs text-amber-700" data-testid="agreement-create-no-templates">No active Service Agreement template is available. Manage templates under Documents &amp; Forms ▸ Document Templates ▸ Agreement Templates.</p>}
            </div>
            <div className="border-t pt-3 space-y-3">
              <Label className="text-sm font-semibold">2. Agreement details</Label>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Start date *</Label>
                  <TrinitiDatePicker value={createForm.start_date} onChange={v => setCreatePeriod('start_date', v)} testId="agreement-create-start" /></div>
                <div><Label>End date *</Label>
                  <TrinitiDatePicker value={createForm.end_date} onChange={v => setCreatePeriod('end_date', v)} testId="agreement-create-end" /></div>
              </div>
              {!onlyBranch(branches) && (   /* one branch: set automatically, no picker */
              <div><Label>Branch *</Label>
                <Select value={createBranchId} onValueChange={(v) => { setCreateBranchId(v); setCreateServices((ls) => ls.filter((l) => l.kind !== 'NDIS')); loadOfficialEnabled(v); }}>
                  <SelectTrigger data-testid="agreement-create-branch"><SelectValue placeholder="Select branch" /></SelectTrigger>
                  <SelectContent>{branches.map(b => <SelectItem key={b.code || b.id} value={b.code || b.id}>{b.name}{b.code ? ` (${b.code})` : ''}</SelectItem>)}</SelectContent>
                </Select>
                <p className="text-[11px] text-muted-foreground mt-1">Determines which NDIS services (Branch Catalogue) can be added below.</p>
              </div>
              )}
              <FundingManagementFields value={createFunding} onChange={setCreateFunding} testIdPrefix="agreement-create-funding" />
              <div><Label>Notes (optional)</Label>
                <Textarea value={createForm.notes} onChange={e => setCreateForm({ ...createForm, notes: e.target.value })} rows={2} data-testid="agreement-create-notes" /></div>
            </div>
            <div className="border-t pt-3" />
            <AgreementServicesEditor lines={createServices} onChange={setCreateServices} enabled={officialEnabled} services={services}
              agreementStart={createForm.start_date} agreementEnd={createForm.end_date} loading={officialLoading}
              branchLabel={branches.find((b) => (b.code || b.id) === createBranchId)?.name || createBranchId} canManageCatalogue={perms.includes('services.manage')} />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCreateOpen(false)} disabled={busy}>Cancel</Button>
            {saveDraftReason(createForm, readiness) && <p className="text-xs text-red-700 mr-auto self-center" data-testid="agreement-create-submit-reason">Save Draft unavailable — {saveDraftReason(createForm, readiness)}</p>}
            <Button onClick={onCreate} disabled={busy || !!saveDraftReason(createForm, readiness)} title={saveDraftReason(createForm, readiness) || 'Saves a DRAFT agreement'} data-testid="agreement-create-submit">Save Draft</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* VARIATION dialog (Day 4 lifecycle: ACTIVE → VARIATION_PENDING + new DRAFT version) */}
      <Dialog open={variationOpen} onOpenChange={setVariationOpen}>
        <DialogContent data-testid="agreement-variation-dialog">
          <DialogHeader>
            <DialogTitle>Start variation — {selected?.agreement_no}</DialogTitle>
            <DialogDescription>Creates version {(selected?.version_no || 1) + 1} as a DRAFT copy (lines, funding, documents). The current version stays active until the variation is signed and activated, then it is superseded.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>New start date</Label><TrinitiDatePicker value={variationForm.start_date} onChange={(v) => setVariationForm({ ...variationForm, start_date: v })} testId="variation-start" /></div>
              <div className="space-y-1"><Label>New end date</Label><TrinitiDatePicker value={variationForm.end_date} onChange={(v) => setVariationForm({ ...variationForm, end_date: v })} testId="variation-end" /></div>
            </div>
            <div className="space-y-1"><Label>Reason for variation</Label><Textarea value={variationForm.reason} onChange={(e) => setVariationForm({ ...variationForm, reason: e.target.value })} data-testid="variation-reason" /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setVariationOpen(false)}>Cancel</Button>
            <Button onClick={onVariation} disabled={busy} data-testid="variation-submit">Create variation draft</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* DETAILS dialog */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto overflow-x-hidden sm:max-w-3xl w-[calc(100vw-2rem)]" data-testid="agreement-details-dialog">
          <DialogHeader>
            <DialogTitle>Agreement {selected?.agreement_no || selected?.id?.slice(-6)}</DialogTitle>
            <DialogDescription>Full details of this service agreement.</DialogDescription>
          </DialogHeader>
          {selected && (
            <div className="space-y-3 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Status</span><StatusBadge status={selected.status} /></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Period</span><span>{selected.start_date} → {selected.end_date}</span></div>
              <div className="flex justify-between" data-testid="agreement-details-template"><span className="text-muted-foreground">Template</span><span>{(detailsData || selected).template ? `${(detailsData || selected).template.name} · v${(detailsData || selected).template.version}` : 'Not selected'}</span></div>
              <div className="flex justify-between items-center"><span className="text-muted-foreground">Funding management</span><FundingBadge type={selected.funding_management?.type} /></div>
              {selected.funding_management?.type === 'PLAN_MANAGED' && selected.funding_management?.plan_manager && (
                <div className="rounded border px-2 py-1 text-xs" data-testid="agreement-details-plan-manager">
                  <p className="font-medium">Plan Manager</p>
                  <p>{[selected.funding_management.plan_manager.name, selected.funding_management.plan_manager.organisation].filter(Boolean).join(' · ')}</p>
                  <p className="text-muted-foreground">{[selected.funding_management.plan_manager.email, selected.funding_management.plan_manager.phone].filter(Boolean).join(' · ')}</p>
                </div>
              )}
              {selected.funding_management?.type === 'SELF_MANAGED' && selected.funding_management?.bill_to?.name && (
                <div className="rounded border px-2 py-1 text-xs" data-testid="agreement-details-nominee">
                  <p className="font-medium">Bill to ({selected.funding_management.bill_to.relationship || 'Nominee'})</p>
                  <p>{selected.funding_management.bill_to.name}</p>
                  <p className="text-muted-foreground">{[selected.funding_management.bill_to.email, selected.funding_management.bill_to.phone].filter(Boolean).join(' · ')}</p>
                </div>
              )}
              <div className="flex justify-between"><span className="text-muted-foreground">Signed by</span><span>{selected.signed_by_name ? `${selected.signed_by_name}${selected.signed_by_role ? ` (${selected.signed_by_role})` : ''}` : '—'}</span></div>
              {selected.sent_to_email && <div className="flex justify-between"><span className="text-muted-foreground">Sent to</span><span>{selected.sent_to_email}</span></div>}
              {selected.review_date && <div className="flex justify-between"><span className="text-muted-foreground">Review date</span><span>{selected.review_date}</span></div>}
              <div>
                {(() => {
                  const det = detailsData || selected;
                  const lines = det.line_items || [];
                  const ft = detailsData?.funding_totals;
                  return (
                    <>
                      {detailsLoading && <p className="text-xs text-muted-foreground" data-testid="agreement-details-loading">Loading details…</p>}
                      {ft && (
                        <div className="rounded-md bg-muted/40 px-3 py-2 mb-2 flex flex-wrap gap-x-6 gap-y-1 text-xs" data-testid="agreement-details-funding-totals">
                          <span><span className="text-muted-foreground">Total funding: </span><span className="font-semibold font-mono">{fmtMoney(ft.total_allocated)}</span></span>
                          <span><span className="text-muted-foreground">Committed: </span><span className="font-mono">{fmtMoney(ft.total_used_committed)}</span></span>
                          <span><span className="text-muted-foreground">Remaining: </span><span className="font-mono">{fmtMoney(ft.total_remaining)}</span></span>
                        </div>
                      )}
                      <p className="text-muted-foreground mb-1">Line items ({lines.length})</p>
                      <div className="space-y-2">
                        {lines.map((li, i) => {
                          const official = li.source === 'OFFICIAL' || li.branch_catalogue_item_id;
                          const snap = li.snapshot;
                          return (
                            <div key={i} className="rounded border px-2 py-2 space-y-1" data-testid={`agreement-details-line-${i}`}>
                              <div className="flex justify-between gap-2 items-start">
                                <span>
                                  <span data-testid={`agreement-details-line-name-${i}`} className="font-medium">{li.service_name || li.name || 'Unknown service'}</span>
                                  {(li.support_item_number || li.ndis_support_item_number) && <span className="ml-2 text-xs text-muted-foreground font-mono">{li.support_item_number || li.ndis_support_item_number}</span>}
                                </span>
                                <Badge variant="outline" className={official ? 'border-primary/40 text-primary text-[10px]' : 'text-[10px]'} data-testid={`agreement-details-line-source-${i}`}>{official ? 'OFFICIAL' : 'CUSTOM'}</Badge>
                              </div>
                              {official ? (
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-1 text-[11px]">
                                  <span><span className="text-muted-foreground">Unit: </span>{li.unit || '—'}</span>
                                  <span><span className="text-muted-foreground">Mode: </span>{(snap?.pricing_mode) || (li.unit_price_one ? 'UNIT_PRICE_ONE' : li.is_quotable ? 'QUOTABLE' : 'PRICED')}</span>
                                  <span><span className="text-muted-foreground">Region: </span>{li.pricing_region || '—'}</span>
                                  <span><span className="text-muted-foreground">Cap: </span><span className="font-mono">{fmtMoney(li.price_cap)}</span></span>
                                  <span><span className="text-muted-foreground">Agreed rate: </span><span className="font-mono">{fmtMoney(li.agreed_rate)}</span></span>
                                  <span><span className="text-muted-foreground">Allocated: </span><span className="font-mono">{fmtMoney(li.allocated_funding)}</span></span>
                                  <span className="md:col-span-2"><span className="text-muted-foreground">Cat. version: </span><span className="font-mono">{li.ndis_version_id || '—'}</span></span>
                                  <span className="md:col-span-2"><span className="text-muted-foreground">Line dates: </span>{li.service_start_date || '—'} → {li.service_end_date || '—'}</span>
                                </div>
                              ) : (
                                <div className="text-[11px] text-muted-foreground">{li.rate_lock?.locked_rate ? `Locked rate $${Number(li.rate_lock.locked_rate).toFixed(2)}/h` : 'Custom catalogue line'}</div>
                              )}
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-1 text-[11px]" data-testid={`agreement-details-line-terms-${i}`}>
                                {li.support_description && <span className="col-span-2 md:col-span-4"><span className="text-muted-foreground">Description: </span>{li.support_description}</span>}
                                <span><span className="text-muted-foreground">Quantity: </span>{li.quantity != null ? `${li.quantity} ${li.unit || ''}`.trim() : NOT_SET}</span>
                                <span><span className="text-muted-foreground">Frequency: </span>{li.frequency ? frequencyLabel(li.frequency) : NOT_SET}</span>
                                <span className="md:col-span-2"><span className="text-muted-foreground">Rate basis: </span>{li.rate_basis ? rateBasisLabel(li.rate_basis) : NOT_SET}{usesCatalogueRate(li) ? ' · Agreed rate: Uses applicable catalogue rate' : (li.agreed_rate != null && li.agreed_rate !== '' ? ` · agreed ${fmtMoney(li.agreed_rate)}` : '')}</span>
                                {!official && li.allocated_funding != null && li.allocated_funding !== '' && <span><span className="text-muted-foreground">Allocated: </span><span className="font-mono">{fmtMoney(li.allocated_funding)}</span></span>}
                                {!official && (li.service_start_date || li.service_end_date) && <span className="md:col-span-3"><span className="text-muted-foreground">Line dates: </span>{li.service_start_date || '—'} → {li.service_end_date || '—'}</span>}
                                <span className="col-span-2 md:col-span-4 text-muted-foreground">
                                  Terms: {[li.allow_cancellation_charge !== false && 'cancellation charge', li.allow_travel && 'provider travel', li.allow_transport && 'activity transport', li.allow_non_face_to_face && 'non face-to-face'].filter(Boolean).join(' · ') || 'none'}
                                </span>
                              </div>
                              {snap && (
                                <div className="mt-1 rounded bg-emerald-50 border border-emerald-200 px-2 py-1 text-[11px]" data-testid={`agreement-details-line-snapshot-${i}`}>
                                  <span className="inline-flex items-center gap-1 font-medium text-emerald-700"><CheckCircle2 className="h-3 w-3" /> Immutable snapshot (locked at activation — not re-priced by later catalogue changes)</span>
                                  <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 mt-0.5 text-emerald-800/90 font-mono">
                                    <span>{snap.support_item_number}</span>
                                    <span>rate {fmtMoney(snap.agreed_rate)}</span>
                                    <span>cap {fmtMoney(snap.applicable_cap)}</span>
                                    <span>{snap.pricing_region || '—'}</span>
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                        {lines.length === 0 && <p className="text-muted-foreground text-xs">No line items.</p>}
                      </div>
                    </>
                  );
                })()}
              </div>
              {selected.notes && <div><p className="text-muted-foreground mb-1">Notes</p><p className="whitespace-pre-wrap">{selected.notes}</p></div>}
              <AgreementDocumentsPanel agreement={detailsData || selected} perms={perms} onAgreementChanged={fetchAgreements} defaultTemplateId={(detailsData || selected).template_id} focus={['upload', 'review'].includes(detailsFocus) ? detailsFocus : null} onFocusConsumed={() => setDetailsFocus(null)} />
              <AgreementLineSchedulePanel agreement={detailsData || selected} perms={perms} fundingPlans={fundingPlans} branches={branches.map(b => ({ id: b.id || b._id, name: b.name }))} focus={detailsFocus === 'schedule' ? 'schedule' : null} onFocusConsumed={() => setDetailsFocus(null)} onScheduled={fetchAgreements} />
              <AgreementHistoryPanel agreement={detailsData || selected} />
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDetailsOpen(false)}>Close</Button>
            {selected?.id && <Button variant="outline" disabled={exporting || !canExport} onClick={() => downloadFrozenDocument(selected, selected.status === 'ACTIVE' || selected.status === 'SIGNED' ? 'signed' : 'unsigned')} title={canExport ? `Download the frozen document as ${agreementDocumentFilename(selected.agreement_no, selected.status === 'ACTIVE' || selected.status === 'SIGNED' ? 'signed' : 'unsigned')}` : 'Requires the agreements.export permission — use View document to open it inline'} data-testid="agreement-details-download"><Download className="h-4 w-4 mr-1" /> Download</Button>}
            {selected?.id && <Button variant="outline" disabled={exporting} onClick={() => exportPdf(selected)} title="Opens the current frozen agreement document (template + organisation branding); generates it first when the status allows" data-testid="agreement-details-pdf">{exporting ? 'Opening…' : 'Export PDF (frozen document)'}</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* FUNDING MANAGEMENT dialog (Phase 4) */}
      <Dialog open={fundingOpen} onOpenChange={setFundingOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="agreement-funding-dialog">
          <DialogHeader>
            <DialogTitle>Funding management — {selected?.agreement_no}</DialogTitle>
            <DialogDescription>Record whether this agreement is self-managed, plan-managed or agency-managed. When funding-method routing is enabled it decides whether the participant, the Plan Manager or the NDIA is billed.</DialogDescription>
          </DialogHeader>
          <FundingManagementFields value={fundingForm} onChange={setFundingForm} testIdPrefix="agreement-funding-form" />
          {fundingPostSignature && fundingCurrentlySet && (
            <p className="text-xs text-amber-700" data-testid="agreement-funding-variation-notice">This agreement is {selected?.status}. The funding arrangement is part of the signed agreement — changes require a controlled variation (new version).</p>
          )}
          {fundingPostSignature && !fundingCurrentlySet && (
            <div data-testid="agreement-funding-correction">
              <Label>Reason for setting the funding method after signature *</Label>
              <Input value={fundingReason} onChange={(e) => setFundingReason(e.target.value)} placeholder="One-time correction — e.g. boxes left unticked at signing" data-testid="agreement-funding-reason" />
              <p className="text-[11px] text-muted-foreground mt-1">Allowed once, by a user with the approve permission; recorded in the audit log.</p>
            </div>
          )}
          {fundingRefusal && (
            <p className="text-xs text-red-700" data-testid="agreement-funding-refusal">{fundingRefusal.code ? `${fundingRefusal.code}: ` : ''}{fundingRefusal.message}</p>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setFundingOpen(false); setFundingRefusal(null); }} disabled={busy}>Cancel</Button>
            <Button onClick={onSaveFunding} disabled={busy} data-testid="agreement-funding-submit">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* EDIT dialog (DRAFT only) */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="agreement-edit-dialog">
          <DialogHeader>
            <DialogTitle>Edit Service Agreement {selected?.agreement_no}</DialogTitle>
            <DialogDescription>Same structure as Create: 1. Template → 2. Agreement details → 3. Services. Existing services keep their identity so linked schedules stay attached.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <AgreementReadinessStrip readiness={editReadiness} perms={perms} extra={[{ key: 'services', label: 'Services', state: (editLines.length + editNewServices.length) ? 'OK' : 'WARN', detail: `${editLines.length} existing service(s)${editNewServices.length ? ` + ${editNewServices.length} to add` : ''}` }]} />

            <div className="space-y-1" data-testid="agreement-edit-template-section">
              <Label className="text-sm font-semibold">1. Template</Label>
              <p className="text-xs text-muted-foreground" data-testid="agreement-edit-template-current">Current: <b>{selected?.template?.name || templates.find((t) => t.id === selected?.template_id)?.name || 'Not selected'}</b>{selected?.template?.version ? ` · v${selected.template.version}` : ''}</p>
              {selected?.status === 'DRAFT' ? (
                <Select value={editForm.template_id || undefined} onValueChange={(v) => setEditForm({ ...editForm, template_id: v })}>
                  <SelectTrigger data-testid="agreement-edit-template"><SelectValue placeholder="Change template (DRAFT only)" /></SelectTrigger>
                  <SelectContent>{templates.map((t) => <SelectItem key={t.id} value={t.id} data-testid={`agreement-edit-template-${t.id}`}>{t.name}{t.shared ? ' (shared sample)' : ` (organisation · v${t.currentVersion || 1})`}{t.sample_state ? ' — SAMPLE, not for signature' : ''}</SelectItem>)}</SelectContent>
                </Select>
              ) : <p className="text-xs text-muted-foreground">The template can only be changed while the agreement is a DRAFT; use a Variation for signed/active agreements.</p>}
              {editForm.template_id && editForm.template_id !== (selected?.template_id || '') && <p className="text-xs text-amber-700" data-testid="agreement-edit-template-changed">Changing the template supersedes any current frozen document — regenerate before sending/signing.</p>}
            </div>

            <div className="border-t pt-3 space-y-3" data-testid="agreement-edit-details-section">
              <Label className="text-sm font-semibold">2. Agreement details</Label>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Start date *</Label>
                  <TrinitiDatePicker value={editForm.start_date} onChange={v => setEditPeriod('start_date', v)} testId="agreement-edit-start" /></div>
                <div><Label>End date *</Label>
                  <TrinitiDatePicker value={editForm.end_date} onChange={v => setEditPeriod('end_date', v)} testId="agreement-edit-end" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label>Branch</Label>
                  <div className="rounded-md border border-dashed bg-muted/40 px-3 py-2 text-sm" data-testid="agreement-edit-branch">
                    {selected?.branch_id ? (branches.find((b) => (b.code || b.id) === selected.branch_id)?.name || selected.branch_id) : <span className="text-muted-foreground">Not set</span>}
                    <span className="block text-[11px] text-muted-foreground">Read-only — determines which NDIS services can be added. Changing branch requires a new agreement.</span>
                  </div></div>
                <div className="space-y-1"><Label>Funding management</Label>
                  <div className="rounded-md border border-dashed bg-muted/40 px-3 py-2 text-sm flex items-start justify-between gap-2" data-testid="agreement-edit-funding">
                    <div>
                      <FundingBadge type={selected?.funding_management?.type} />
                      {selected?.funding_management?.plan_manager?.name && <span className="block text-[11px] text-muted-foreground" data-testid="agreement-edit-funding-pm">Plan manager: {selected.funding_management.plan_manager.name}</span>}
                      {!selected?.funding_management?.type && <span className="block text-[11px] text-muted-foreground">Not set</span>}
                    </div>
                    <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => { setFundingForm(fundingFromAgreement(selected)); setFundingOpen(true); }} disabled={busy} data-testid="agreement-edit-funding-open">Edit funding</Button>
                  </div></div>
              </div>
              <div><Label>Notes (optional)</Label>
                <Textarea value={editForm.notes} onChange={e => setEditForm({ ...editForm, notes: e.target.value })} rows={2} data-testid="agreement-edit-notes" /></div>
            </div>

            <div className="border-t pt-3 space-y-3" data-testid="agreement-edit-services-section">
              <Label className="text-sm font-semibold">3. Services</Label>
              <AgreementLineTermsEditor lines={editLines} onChange={setEditLines} />
              <AgreementServicesEditor lines={editNewServices} onChange={setEditNewServices} enabled={officialEnabled} services={services}
                agreementStart={editForm.start_date} agreementEnd={editForm.end_date} loading={officialLoading} title="Add services"
                emptyHint={<>Use <b>Add Service</b> to add an NDIS or custom service to this draft. Existing services above keep their identity; removing a service is not available here.</>}
                branchLabel={branches.find((b) => (b.code || b.id) === selected?.branch_id)?.name || selected?.branch_id} canManageCatalogue={perms.includes('services.manage')} />
              {editNewServices.length > 0 && <div className="flex justify-end text-sm border-t pt-2" data-testid="agreement-edit-funding-total"><span className="text-muted-foreground mr-2">Allocated funding total (existing + added):</span><span className="font-semibold font-mono">{fmtMoney([...editLines, ...editNewServices].reduce((sum, l) => sum + (Number(l.allocated_funding) || 0), 0))}</span></div>}
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditOpen(false)} disabled={busy}>Cancel</Button>
            <Button variant="outline" onClick={() => setPreviewFor(selected)} disabled={busy || !selected} data-testid="agreement-edit-preview"><Eye className="h-3.5 w-3.5 mr-1" /> Preview Agreement</Button>
            <Button onClick={onEditSave} disabled={busy || !editForm.start_date || !editForm.end_date} data-testid="agreement-edit-submit">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default ParticipantAgreementsTab;
